import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { message } from 'antd';
import { installSafeMessageHandlers } from './utils/safeMessage';
import { getPersianHolidaysMap } from './utils/persianHolidaysCache';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { AttendanceProvider } from './contexts/AttendanceContext';
import { CallCenterProvider } from './contexts/CallCenterContext';
import { SocketProvider } from './contexts/SocketContext';
import { useAuth } from './contexts/AuthContext';
import FollowUpReminderPoller from './components/notifications/FollowUpReminderPoller';
import SmsNotificationPoller from './components/notifications/SmsNotificationPoller';
import CallScreenPopPoller from './components/call-center/CallScreenPopPoller';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import PrivateDashboard from './pages/dashboard/PrivateDashboard';
import AdvancedDashboard from './pages/dashboard/AdvancedDashboard';
import AllFollowUps from './pages/dashboard/AllFollowUps';
import SuperAdminDashboard from './pages/dashboard/SuperAdminDashboard';
import RouteFallback from './components/common/RouteFallback';
import LeadForm from './pages/leads/LeadForm';
import AddDeal from './pages/deals/AddDeal';
import ViewDeal from './pages/deals/ViewDeal';
import Calendar from './pages/Calendar';
import Tasks from './pages/work/tasks/Tasks';
import AddTask from './pages/work/tasks/AddTask';
import EditTask from './pages/work/tasks/EditTask';
import ViewTask from './pages/work/tasks/ViewTask';
import TaskSettings from './pages/work/tasks/TaskSettings';
import TaskBoardSettings from './pages/work/tasks/TaskBoardSettings';
import Projects from './pages/work/projects/Projects';
import ProjectForm from './pages/work/projects/ProjectForm';
import ViewProject from './pages/work/projects/ViewProject';
import ProjectTemplates from './pages/work/projects/ProjectTemplates';
import ProjectTemplateForm from './pages/work/projects/ProjectTemplateForm';
import ProjectTemplateView from './pages/work/projects/ProjectTemplateView';
import Contracts from './pages/work/contracts/Contracts';
import ContractForm from './pages/work/contracts/ContractForm';
import ViewContract from './pages/work/contracts/ViewContract';
import ContractTemplates from './pages/work/contracts/ContractTemplates';
import ContractTemplateForm from './pages/work/contracts/ContractTemplateForm';
import ContractTemplateView from './pages/work/contracts/ContractTemplateView';
import HrDashboard from './pages/hr/dashboard/HrDashboard';
import Employees from './pages/hr/employees/Employees';
import AddEmployee from './pages/hr/employees/AddEmployee';
import EditEmployee from './pages/hr/employees/EditEmployee';
import ViewEmployee from './pages/hr/employees/ViewEmployee';
import Leaves from './pages/hr/leaves/Leaves';
import AddLeave from './pages/hr/leaves/AddLeave';
import ViewLeave from './pages/hr/leaves/ViewLeave';
import EditLeave from './pages/hr/leaves/EditLeave';
import LeavesSettings from './pages/hr/leaves/LeavesSettings';
import AttendanceSettings from './pages/hr/attendance/AttendanceSettings';
import Shifts from './pages/hr/shifts/Shifts';
import Attendance from './pages/hr/attendance/Attendance';
import RegisterAttendance from './pages/hr/attendance/RegisterAttendance';
import AttendanceRegularizations from './pages/hr/attendance/AttendanceRegularizations';
import AttendanceAdjustmentsReport from './pages/hr/attendance/AttendanceAdjustmentsReport';
import OvertimeRequests from './pages/hr/overtime/OvertimeRequests';
import MissionRequests from './pages/hr/missions/MissionRequests';
import WorkflowInbox from './pages/workflow/WorkflowInbox';
import Holidays from './pages/hr/holidays/Holidays';
import AddHoliday from './pages/hr/holidays/AddHoliday';
import EditHoliday from './pages/hr/holidays/EditHoliday';
import Departments from './pages/hr/departments/Departments';
import AddDepartment from './pages/hr/departments/AddDepartment';
import EditDepartment from './pages/hr/departments/EditDepartment';
import ViewDepartment from './pages/hr/departments/ViewDepartment';
import Positions from './pages/hr/positions/Positions';
import AddPosition from './pages/hr/positions/AddPosition';
import EditPosition from './pages/hr/positions/EditPosition';
import ViewPosition from './pages/hr/positions/ViewPosition';
import Clients from './pages/clients/Clients';
import AddClient from './pages/clients/AddClient';
import EditClient from './pages/clients/EditClient';
import ViewClient from './pages/clients/ViewClient';
import Products from './pages/products/Products';
import AddProduct from './pages/products/AddProduct';
import EditProduct from './pages/products/EditProduct';
import ViewProduct from './pages/products/ViewProduct';
import Orders from './pages/orders/Orders';
import AddOrder from './pages/orders/AddOrder';
import EditOrder from './pages/orders/EditOrder';
import ViewOrder from './pages/orders/ViewOrder';
import Tickets from './pages/tickets/Tickets';
import AddTicket from './pages/tickets/AddTicket';
import ViewTicket from './pages/tickets/ViewTicket';
import TicketFormSettings from './pages/tickets/TicketFormSettings';
import TicketSettings from './pages/tickets/TicketSettings';
import CallCenterSettings from './pages/call-center/CallCenterSettings';
import CallCenterAgents from './pages/call-center/CallCenterAgents';
import CallCenterCalls from './pages/call-center/CallCenterCalls';
import ViewCallCenterCall from './pages/call-center/ViewCallCenterCall';
import CallCenterBlacklist from './pages/call-center/CallCenterBlacklist';
import CallCenterReports from './pages/call-center/CallCenterReports';
import Notices from './pages/notices/Notices';
import AddNotice from './pages/notices/AddNotice';
import EditNotice from './pages/notices/EditNotice';
import ViewNotice from './pages/notices/ViewNotice';
import Messages from './pages/messages/Messages';
import ViewMessage from './pages/messages/ViewMessage';
import CompanySettings from './pages/settings/CompanySettings';
import BusinessAddresses from './pages/settings/BusinessAddresses';
import CurrencySettings from './pages/settings/CurrencySettings';
import PackagesSettings from './pages/settings/PackagesSettings';
import CompaniesSettings from './pages/settings/CompaniesSettings';
import CompanyCreate from './pages/settings/CompanyCreate';
import CompanyDetail from './pages/settings/CompanyDetail';
import CompanyEdit from './pages/settings/CompanyEdit';
import SuperadminInvoicesSettings from './pages/settings/SuperadminInvoicesSettings';
import SuperadminFaqsSettings from './pages/settings/SuperadminFaqsSettings';
import SuperadminFaqCreate from './pages/settings/SuperadminFaqCreate';
import SuperadminFaqShow from './pages/settings/SuperadminFaqShow';
import SuperadminFaqEdit from './pages/settings/SuperadminFaqEdit';
import SuperadminUsersSettings from './pages/settings/SuperadminUsersSettings';
import SuperadminUserCreate from './pages/settings/SuperadminUserCreate';
import SuperadminUserEdit from './pages/settings/SuperadminUserEdit';
import SupportTicketsSettings from './pages/settings/SupportTicketsSettings';
import SupportTicketCreate from './pages/settings/SupportTicketCreate';
import SupportTicketShow from './pages/settings/SupportTicketShow';
import SuperadminAppSettings from './pages/settings/superadmin/SuperadminAppSettings';
import SuperadminProfileSettings from './pages/settings/superadmin/SuperadminProfileSettings';
import SuperadminGlobalInvoiceSettings from './pages/settings/superadmin/SuperadminGlobalInvoiceSettings';
import SuperadminLanguageSettings from './pages/settings/superadmin/SuperadminLanguageSettings';
import SuperadminGlobalCurrencySettings from './pages/settings/superadmin/SuperadminGlobalCurrencySettings';
import ProfileSettings from './pages/settings/ProfileSettings';
import AppSettings from './pages/settings/AppSettings';
import RolePermissionsSettings from './pages/settings/RolePermissionsSettings';
import FinanceReport from './pages/reports/FinanceReport';
import IncomeExpenseReport from './pages/reports/IncomeExpenseReport';
import LeaveReport from './pages/reports/LeaveReport';
import LeaveQuotaReport from './pages/reports/LeaveQuotaReport';
import LeaveQuotaDetailReport from './pages/reports/LeaveQuotaDetailReport';
import AttendanceReport from './pages/reports/AttendanceReport';
import ExpenseReport from './pages/reports/ExpenseReport';
import LeadReport from './pages/reports/LeadReport';
import SalesReport from './pages/reports/SalesReport';
import ReportsIndex from './pages/reports/ReportsIndex';
import Action from './pages/action/Action';
import CreateAction from './pages/action/CreateAction';
import EditAction from './pages/action/EditAction';
import ActionDashboard from './pages/action/ActionDashboard';
import MyActions from './pages/action/MyActions';
import Notifications from './pages/action/Notifications';
import NotificationSettings from './pages/action/NotificationSettings';
import ActionReports from './pages/action/ActionReports';
import BankAccounts from './pages/finance/BankAccounts';
import AddBankAccount from './pages/finance/AddBankAccount';
import EditBankAccount from './pages/finance/EditBankAccount';
import ViewBankAccount from './pages/finance/ViewBankAccount';
import Expenses from './pages/finance/Expenses';
import PettyCash from './pages/finance/PettyCash';
import AddExpense from './pages/finance/AddExpense';
import EditExpense from './pages/finance/EditExpense';
import ViewExpense from './pages/finance/ViewExpense';
import RecurringExpenses from './pages/finance/RecurringExpenses';
import AddRecurringExpense from './pages/finance/AddRecurringExpense';
import EditRecurringExpense from './pages/finance/EditRecurringExpense';
import ViewRecurringExpense from './pages/finance/ViewRecurringExpense';
import PaymentGateway from './pages/finance/PaymentGateway';
import TaxSettings from './pages/finance/TaxSettings';
import InvoiceSettings from './pages/finance/InvoiceSettings';
import Estimates from './pages/finance/Estimates';
import AddEstimate from './pages/finance/AddEstimate';
import EditEstimate from './pages/finance/EditEstimate';
import Invoices from './pages/finance/Invoices';
import AddInvoice from './pages/finance/AddInvoice';
import EditInvoice from './pages/finance/EditInvoice';
import ViewInvoice from './pages/finance/ViewInvoice';
import FinanceDashboard from './pages/finance/FinanceDashboard';
import Approvals from './pages/finance/Approvals';
import Transactions from './pages/finance/Transactions';
import AuditLogs from './pages/finance/AuditLogs';
import RegisterDealPayment from './pages/finance/RegisterDealPayment';
import ChequeVault from './pages/finance/ChequeVault';
import ReceivablesSummary from './pages/finance/ReceivablesSummary';
import AgingReport from './pages/finance/AgingReport';
import DealInstallments from './pages/finance/DealInstallments';
import Targets from './pages/finance/Targets';
import CommissionPlans from './pages/finance/CommissionPlans';
import Payroll from './pages/finance/Payroll';
import Refunds from './pages/finance/Refunds';
import DiscountLimits from './pages/finance/DiscountLimits';
import ClientFinancialProfile from './pages/finance/ClientFinancialProfile';
import './App.css';

const Leads = lazy(() => import('./pages/Leads'));
const AddLead = lazy(() => import('./pages/leads/AddLead'));
const EditLead = lazy(() => import('./pages/leads/EditLead'));
const ViewLead = lazy(() => import('./pages/leads/ViewLead'));
const ImportLeads = lazy(() => import('./pages/leads/ImportLeads'));
const ImportDeals = lazy(() => import('./pages/deals/ImportDeals'));
const ImportEmployees = lazy(() => import('./pages/hr/employees/ImportEmployees'));
const Deals = lazy(() => import('./pages/leads/Deals'));
const LeadSettings = lazy(() => import('./pages/leads/LeadSettings'));
const LeadsReports = lazy(() => import('./pages/leads/LeadsReports'));
const UnmatchedSmsInbox = lazy(() => import('./pages/sms/UnmatchedSmsInbox'));
const LeadFormBuilder = lazy(() => import('./pages/leads/leadForms/LeadFormBuilder'));
const LeadFormEditor = lazy(() => import('./pages/leads/leadForms/LeadFormEditor'));
const LeadFormDetail = lazy(() => import('./pages/leads/leadForms/LeadFormDetail'));

const withRouteSuspense = (Component) => (
  <Suspense fallback={<RouteFallback />}>
    <Component />
  </Suspense>
);

function AuthenticatedBootstrap() {
  const { isAuthenticated, token } = useAuth();

  useEffect(() => {
    if (!isAuthenticated || !token) return;
    getPersianHolidaysMap();
  }, [isAuthenticated, token]);

  return null;
}

function App() {
  // تنظیم مدت زمان نمایش نوتیفیکیشن‌ها به 6 ثانیه (3 ثانیه بیشتر از پیش‌فرض)
  useEffect(() => {
    installSafeMessageHandlers();
    message.config({
      duration: 6,
      maxCount: 3,
    });
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <SocketProvider>
          <AttendanceProvider>
          <PermissionsProvider>
          <NotificationProvider>
          <CallCenterProvider>
          <BrowserRouter>
            <SidebarProvider>
              <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/private"
              element={
                <ProtectedRoute>
                  <PrivateDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/advanced"
              element={
                <ProtectedRoute>
                  <AdvancedDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/follow-ups"
              element={
                <ProtectedRoute>
                  <AllFollowUps />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/super-admin"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <SuperAdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads"
              element={
                <ProtectedRoute>
                  {withRouteSuspense(Leads)}
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads/add"
              element={
                <ProtectedRoute>
                  {withRouteSuspense(AddLead)}
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads/create"
              element={
                <ProtectedRoute>
                  {withRouteSuspense(AddLead)}
                </ProtectedRoute>
              }
            />
              <Route
              path="/leads/reports"
              element={
                <ProtectedRoute>
                  {withRouteSuspense(LeadsReports)}
                </ProtectedRoute>
              }
            />
              <Route
              path="/sms/unmatched"
              element={
                <ProtectedRoute>
                  {withRouteSuspense(UnmatchedSmsInbox)}
                </ProtectedRoute>
              }
            />
              <Route
              path="/leads/:id/edit"
              element={
                <ProtectedRoute>
                  {withRouteSuspense(EditLead)}
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads/:id"
              element={
                <ProtectedRoute>
                  {withRouteSuspense(ViewLead)}
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads/import"
              element={
                <ProtectedRoute>
                  {withRouteSuspense(ImportLeads)}
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads/lead-forms/create"
              element={
                <ProtectedRoute>
                  {withRouteSuspense(LeadFormEditor)}
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads/lead-forms/:id/edit"
              element={
                <ProtectedRoute>
                  {withRouteSuspense(LeadFormEditor)}
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads/lead-forms/:id"
              element={
                <ProtectedRoute>
                  {withRouteSuspense(LeadFormDetail)}
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads/lead-forms"
              element={
                <ProtectedRoute>
                  {withRouteSuspense(LeadFormBuilder)}
                </ProtectedRoute>
              }
            />
            <Route
              path="/deals/import"
              element={
                <ProtectedRoute>
                  {withRouteSuspense(ImportDeals)}
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads/form"
              element={
                <ProtectedRoute>
                  <LeadForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads/deals"
              element={
                <ProtectedRoute>
                  {withRouteSuspense(Deals)}
                </ProtectedRoute>
              }
            />
            <Route
              path="/deals/add"
              element={
                <ProtectedRoute>
                  <AddDeal />
                </ProtectedRoute>
              }
            />
            <Route
              path="/deals/:id"
              element={
                <ProtectedRoute>
                  <ViewDeal />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads/settings"
              element={
                <ProtectedRoute>
                  {withRouteSuspense(LeadSettings)}
                </ProtectedRoute>
              }
            />
            <Route
              path="/calendar"
              element={
                <ProtectedRoute>
                  <Calendar />
                </ProtectedRoute>
              }
            />
            <Route path="/work" element={<Navigate to="/work/tasks" replace />} />
            <Route path="/work/tasks" element={<ProtectedRoute requireModule="tasks"><Tasks /></ProtectedRoute>} />
            <Route path="/work/tasks/add" element={<ProtectedRoute requireModule="tasks"><AddTask /></ProtectedRoute>} />
            <Route path="/work/tasks/settings" element={<ProtectedRoute requireModule="tasks"><TaskSettings /></ProtectedRoute>} />
            <Route path="/work/tasks/board-settings" element={<ProtectedRoute requireModule="tasks"><TaskBoardSettings /></ProtectedRoute>} />
            <Route path="/work/tasks/:id/edit" element={<ProtectedRoute requireModule="tasks"><EditTask /></ProtectedRoute>} />
            <Route path="/work/tasks/:id" element={<ProtectedRoute requireModule="tasks"><ViewTask /></ProtectedRoute>} />
            <Route path="/work/projects" element={<ProtectedRoute requireModule="projects"><Projects /></ProtectedRoute>} />
            <Route path="/work/projects/add" element={<ProtectedRoute requireModule="projects"><ProjectForm /></ProtectedRoute>} />
            <Route path="/work/projects/settings" element={<Navigate to="/work/projects/templates" replace />} />
            <Route path="/work/projects/templates" element={<ProtectedRoute requireModule="projects"><ProjectTemplates /></ProtectedRoute>} />
            <Route path="/work/projects/templates/new" element={<ProtectedRoute requireModule="projects"><ProjectTemplateForm /></ProtectedRoute>} />
            <Route path="/work/projects/templates/:id/edit" element={<ProtectedRoute requireModule="projects"><ProjectTemplateForm /></ProtectedRoute>} />
            <Route path="/work/projects/templates/:id" element={<ProtectedRoute requireModule="projects"><ProjectTemplateView /></ProtectedRoute>} />
            <Route path="/work/projects/:id/edit" element={<ProtectedRoute requireModule="projects"><ProjectForm /></ProtectedRoute>} />
            <Route path="/work/projects/:id" element={<ProtectedRoute requireModule="projects"><ViewProject /></ProtectedRoute>} />
            <Route path="/work/contracts" element={<ProtectedRoute requireModule="contracts"><Contracts /></ProtectedRoute>} />
            <Route path="/work/contracts/add" element={<ProtectedRoute requireModule="contracts"><ContractForm /></ProtectedRoute>} />
            <Route path="/work/contracts/settings" element={<Navigate to="/work/contracts/templates" replace />} />
            <Route path="/work/contracts/templates" element={<ProtectedRoute requireModule="contracts"><ContractTemplates /></ProtectedRoute>} />
            <Route path="/work/contracts/templates/new" element={<ProtectedRoute requireModule="contracts"><ContractTemplateForm /></ProtectedRoute>} />
            <Route path="/work/contracts/templates/:id/edit" element={<ProtectedRoute requireModule="contracts"><ContractTemplateForm /></ProtectedRoute>} />
            <Route path="/work/contracts/templates/:id" element={<ProtectedRoute requireModule="contracts"><ContractTemplateView /></ProtectedRoute>} />
            <Route path="/work/contracts/:id/edit" element={<ProtectedRoute requireModule="contracts"><ContractForm /></ProtectedRoute>} />
            <Route path="/work/contracts/:id" element={<ProtectedRoute requireModule="contracts"><ViewContract /></ProtectedRoute>} />
            <Route
              path="/hr"
              element={<Navigate to="/hr/dashboard" replace />}
            />
            <Route
              path="/hr/dashboard"
              element={
                <ProtectedRoute>
                  <HrDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/employees"
              element={
                <ProtectedRoute>
                  <Employees />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/employees/import"
              element={
                <ProtectedRoute>
                  {withRouteSuspense(ImportEmployees)}
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/employees/add"
              element={
                <ProtectedRoute>
                  <AddEmployee />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/employees/:id/edit"
              element={
                <ProtectedRoute>
                  <EditEmployee />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/employees/:id"
              element={
                <ProtectedRoute>
                  <ViewEmployee />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/leaves"
              element={
                <ProtectedRoute>
                  <Leaves />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/leaves/add"
              element={
                <ProtectedRoute>
                  <AddLeave />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/leaves/:id"
              element={
                <ProtectedRoute>
                  <ViewLeave />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/leaves/:id/edit"
              element={
                <ProtectedRoute>
                  <EditLeave />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/leaves-settings"
              element={
                <ProtectedRoute>
                  <LeavesSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/attendance-settings"
              element={
                <ProtectedRoute>
                  <AttendanceSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/shifts"
              element={
                <ProtectedRoute>
                  <Shifts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/attendance"
              element={
                <ProtectedRoute>
                  <Attendance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/attendance/register"
              element={
                <ProtectedRoute>
                  <RegisterAttendance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/attendance-regularizations"
              element={
                <ProtectedRoute>
                  <AttendanceRegularizations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/attendance-adjustments"
              element={
                <ProtectedRoute>
                  <AttendanceAdjustmentsReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/overtime-requests"
              element={
                <ProtectedRoute>
                  <OvertimeRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/attendance-missions"
              element={
                <ProtectedRoute>
                  <MissionRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workflow/inbox"
              element={
                <ProtectedRoute>
                  <WorkflowInbox />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/holidays"
              element={
                <ProtectedRoute>
                  <Holidays />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/holidays/add"
              element={
                <ProtectedRoute>
                  <AddHoliday />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/holidays/:id/edit"
              element={
                <ProtectedRoute>
                  <EditHoliday />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/departments"
              element={
                <ProtectedRoute>
                  <Departments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/departments/add"
              element={
                <ProtectedRoute>
                  <AddDepartment />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/departments/:id/edit"
              element={
                <ProtectedRoute>
                  <EditDepartment />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/departments/:id"
              element={
                <ProtectedRoute>
                  <ViewDepartment />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/positions"
              element={
                <ProtectedRoute>
                  <Positions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/positions/add"
              element={
                <ProtectedRoute>
                  <AddPosition />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/positions/:id/edit"
              element={
                <ProtectedRoute>
                  <EditPosition />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/positions/:id"
              element={
                <ProtectedRoute>
                  <ViewPosition />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients"
              element={
                <ProtectedRoute>
                  <Clients />
                </ProtectedRoute>
              }
            />
            <Route path="/clients/categories" element={<Navigate to="/clients" replace />} />
            <Route path="/clients/subcategories" element={<Navigate to="/clients" replace />} />
            <Route
              path="/clients/add"
              element={
                <ProtectedRoute>
                  <AddClient />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients/create"
              element={
                <ProtectedRoute>
                  <AddClient />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients/:id/edit"
              element={
                <ProtectedRoute>
                  <EditClient />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients/:id"
              element={
                <ProtectedRoute>
                  <ViewClient />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products"
              element={
                <ProtectedRoute>
                  <Products />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products/add"
              element={
                <ProtectedRoute>
                  <AddProduct />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products/:id"
              element={
                <ProtectedRoute>
                  <ViewProduct />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products/:id/edit"
              element={
                <ProtectedRoute>
                  <EditProduct />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <ProtectedRoute>
                  <Orders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders/add"
              element={
                <ProtectedRoute>
                  <AddOrder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders/:id"
              element={
                <ProtectedRoute>
                  <ViewOrder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders/:id/edit"
              element={
                <ProtectedRoute>
                  <EditOrder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tickets/add"
              element={
                <ProtectedRoute requireModule="tickets">
                  <AddTicket />
                </ProtectedRoute>
              }
            />
            <Route
              path="/call-center/reports"
              element={
                <ProtectedRoute requireModule="callcenter" permission="view_callcenter_reports">
                  <CallCenterReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/call-center/blacklist"
              element={
                <ProtectedRoute requireModule="callcenter" permission="manage_callcenter_blacklist">
                  <CallCenterBlacklist />
                </ProtectedRoute>
              }
            />
            <Route
              path="/call-center/calls/:id"
              element={
                <ProtectedRoute requireModule="callcenter" permission="view_callcenter">
                  <ViewCallCenterCall />
                </ProtectedRoute>
              }
            />
            <Route
              path="/call-center/calls"
              element={
                <ProtectedRoute requireModule="callcenter" permission="view_callcenter">
                  <CallCenterCalls />
                </ProtectedRoute>
              }
            />
            <Route
              path="/call-center/agents"
              element={
                <ProtectedRoute requireModule="callcenter" permission="manage_callcenter_agents">
                  <CallCenterAgents />
                </ProtectedRoute>
              }
            />
            <Route
              path="/call-center/settings"
              element={
                <ProtectedRoute requireModule="callcenter" permission="manage_callcenter_settings">
                  <CallCenterSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tickets/ticket-form"
              element={
                <ProtectedRoute requireModule="tickets">
                  <TicketFormSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tickets/settings"
              element={
                <ProtectedRoute requireModule="tickets">
                  <TicketSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tickets/:id"
              element={
                <ProtectedRoute requireModule="tickets">
                  <ViewTicket />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tickets"
              element={
                <ProtectedRoute requireModule="tickets">
                  <Tickets />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notices"
              element={
                <ProtectedRoute>
                  <Notices />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notices/add"
              element={
                <ProtectedRoute>
                  <AddNotice />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notices/:id/edit"
              element={
                <ProtectedRoute>
                  <EditNotice />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notices/:id"
              element={
                <ProtectedRoute>
                  <ViewNotice />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages"
              element={
                <ProtectedRoute>
                  <Messages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages/:id"
              element={
                <ProtectedRoute>
                  <ViewMessage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/company"
              element={
                <ProtectedRoute>
                  <CompanySettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/business-address"
              element={
                <ProtectedRoute>
                  <BusinessAddresses />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/currency-settings"
              element={
                <ProtectedRoute>
                  <CurrencySettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/profile-settings"
              element={
                <ProtectedRoute>
                  <ProfileSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/app-settings"
              element={
                <ProtectedRoute>
                  <AppSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/role-permissions"
              element={
                <ProtectedRoute>
                  <RolePermissionsSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/packages"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <PackagesSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/companies"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <CompaniesSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/companies/create"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <CompanyCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/companies/:id/edit"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <CompanyEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/companies/:id"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <CompanyDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/superadmin-invoices"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <SuperadminInvoicesSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/superadmin-faqs"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <SuperadminFaqsSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/superadmin-faqs/create"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <SuperadminFaqCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/superadmin-faqs/:id"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <SuperadminFaqShow />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/superadmin-faqs/:id/edit"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <SuperadminFaqEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/superadmin-users"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <SuperadminUsersSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/superadmin-users/create"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <SuperadminUserCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/superadmin-users/:id/edit"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <SuperadminUserEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/support-tickets"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <SupportTicketsSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/support-tickets/create"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <SupportTicketCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/support-tickets/:id"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <SupportTicketShow />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/superadmin/app-settings"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <SuperadminAppSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/superadmin/profile"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <SuperadminProfileSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/superadmin/invoice"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <SuperadminGlobalInvoiceSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/superadmin/language"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <SuperadminLanguageSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/superadmin/currency"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <SuperadminGlobalCurrencySettings />
                </ProtectedRoute>
              }
            />
            <Route path="/reports" element={<ProtectedRoute><ReportsIndex /></ProtectedRoute>} />
            <Route path="/reports/finance" element={<ProtectedRoute><FinanceReport /></ProtectedRoute>} />
            <Route path="/reports/income-expense" element={<ProtectedRoute><IncomeExpenseReport /></ProtectedRoute>} />
            <Route path="/reports/leaves" element={<ProtectedRoute><LeaveReport /></ProtectedRoute>} />
            <Route path="/reports/leave-quota" element={<ProtectedRoute><LeaveQuotaReport /></ProtectedRoute>} />
            <Route path="/reports/leave-quota/:employeeId/:year/:month" element={<ProtectedRoute><LeaveQuotaDetailReport /></ProtectedRoute>} />
            <Route path="/reports/attendance" element={<ProtectedRoute><AttendanceReport /></ProtectedRoute>} />
            <Route path="/reports/expenses" element={<ProtectedRoute><ExpenseReport /></ProtectedRoute>} />
            <Route path="/reports/leads" element={<ProtectedRoute><LeadReport /></ProtectedRoute>} />
            <Route path="/reports/sales" element={<ProtectedRoute><SalesReport /></ProtectedRoute>} />
            <Route
              path="/action"
              element={
                <ProtectedRoute>
                  <Action />
                </ProtectedRoute>
              }
            />
            <Route
              path="/action/dashboard"
              element={
                <ProtectedRoute>
                  <ActionDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/action/my-actions"
              element={
                <ProtectedRoute>
                  <MyActions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/action/create"
              element={
                <ProtectedRoute>
                  <CreateAction />
                </ProtectedRoute>
              }
            />
            <Route
              path="/action/:id"
              element={
                <ProtectedRoute>
                  <EditAction />
                </ProtectedRoute>
              }
            />
            <Route
              path="/action/notifications"
              element={
                <ProtectedRoute>
                  <Notifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/action/notifications/settings"
              element={
                <ProtectedRoute>
                  <NotificationSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/action/reports"
              element={
                <ProtectedRoute>
                  <ActionReports />
                </ProtectedRoute>
              }
            />
            {/* Finance Routes */}
            <Route
              path="/finance/dashboard"
              element={
                <ProtectedRoute>
                  <FinanceDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/approvals"
              element={
                <ProtectedRoute>
                  <Approvals />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/transactions"
              element={
                <ProtectedRoute>
                  <Transactions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/audit-logs"
              element={
                <ProtectedRoute>
                  <AuditLogs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/register-payment"
              element={
                <ProtectedRoute>
                  <RegisterDealPayment />
                </ProtectedRoute>
              }
            />
            {/* Phase 3 Routes */}
            <Route
              path="/finance/cheques"
              element={
                <ProtectedRoute>
                  <ChequeVault />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/receivables-summary"
              element={
                <ProtectedRoute>
                  <ReceivablesSummary />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/aging-report"
              element={
                <ProtectedRoute>
                  <AgingReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/deals/:dealId/installments"
              element={
                <ProtectedRoute>
                  <DealInstallments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/bank-accounts"
              element={
                <ProtectedRoute>
                  <BankAccounts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/bank-accounts/add"
              element={
                <ProtectedRoute>
                  <AddBankAccount />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/bank-accounts/:id"
              element={
                <ProtectedRoute>
                  <ViewBankAccount />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/bank-accounts/:id/edit"
              element={
                <ProtectedRoute>
                  <EditBankAccount />
                </ProtectedRoute>
              }
            />
            {/* Petty Cash Routes */}
            <Route
              path="/finance/petty-cash"
              element={
                <ProtectedRoute>
                  <PettyCash />
                </ProtectedRoute>
              }
            />
            {/* Expenses Routes */}
            <Route
              path="/finance/expenses"
              element={
                <ProtectedRoute>
                  <Expenses />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/expenses/add"
              element={
                <ProtectedRoute>
                  <AddExpense />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/expenses/:id"
              element={
                <ProtectedRoute>
                  <ViewExpense />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/expenses/:id/edit"
              element={
                <ProtectedRoute>
                  <EditExpense />
                </ProtectedRoute>
              }
            />
            {/* Recurring Expenses Routes */}
            <Route
              path="/finance/recurring-expenses"
              element={
                <ProtectedRoute>
                  <RecurringExpenses />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/recurring-expenses/add"
              element={
                <ProtectedRoute>
                  <AddRecurringExpense />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/recurring-expenses/:id"
              element={
                <ProtectedRoute>
                  <ViewRecurringExpense />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/recurring-expenses/:id/edit"
              element={
                <ProtectedRoute>
                  <EditRecurringExpense />
                </ProtectedRoute>
              }
            />
            {/* Phase 5 Routes */}
            <Route
              path="/finance/targets"
              element={
                <ProtectedRoute>
                  <Targets />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/commission-plans"
              element={
                <ProtectedRoute>
                  <CommissionPlans />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/payroll"
              element={
                <ProtectedRoute>
                  <Payroll />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/payment-gateway"
              element={
                <ProtectedRoute>
                  <PaymentGateway />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/tax"
              element={
                <ProtectedRoute>
                  <TaxSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/estimates/add"
              element={
                <ProtectedRoute>
                  <AddEstimate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/estimates/:id/edit"
              element={
                <ProtectedRoute>
                  <EditEstimate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/estimates"
              element={
                <ProtectedRoute>
                  <Estimates />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/invoices"
              element={
                <ProtectedRoute>
                  <Invoices />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/invoices/add"
              element={
                <ProtectedRoute>
                  <AddInvoice />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/invoices/:id"
              element={
                <ProtectedRoute>
                  <ViewInvoice />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/invoices/:id/edit"
              element={
                <ProtectedRoute>
                  <EditInvoice />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/invoice-settings"
              element={
                <ProtectedRoute>
                  <InvoiceSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/refunds"
              element={
                <ProtectedRoute>
                  <Refunds />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/discount-limits"
              element={
                <ProtectedRoute>
                  <DiscountLimits />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/client-profile/:id"
              element={
                <ProtectedRoute>
                  <ClientFinancialProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/client-profile"
              element={
                <ProtectedRoute>
                  <ClientFinancialProfile />
                </ProtectedRoute>
              }
            />
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              </Routes>
              <AuthenticatedBootstrap />
              <FollowUpReminderPoller />
              <SmsNotificationPoller />
              <CallScreenPopPoller />
            </SidebarProvider>
          </BrowserRouter>
          </CallCenterProvider>
          </NotificationProvider>
        </PermissionsProvider>
        </AttendanceProvider>
        </SocketProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
